/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// ESTIMATE PHASES HOOK - React Query implementation for estimate phases
// ============================================================================

"use client";

import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { useOrganizationQueryGuard } from "@/hooks/use-organization-query";
import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import { toastFromApiFailure } from "@/lib/api/toast-utils";
import { QUERY_GC_TIME, QUERY_STALE_TIME } from "@/lib/constants/query-config";
import { logger } from "@/lib/utils/logger";
import type {
  PhaseCreateInput,
  PhaseResponse,
  PhaseUpdateInput,
} from "@/lib/validations/api-schemas/phases";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "sonner";

interface UseEstimatePhasesResult {
  phases: PhaseResponse[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  createPhase: (input: PhaseCreateInput) => Promise<PhaseResponse | null>;
  updatePhase: (
    phaseId: string,
    input: PhaseUpdateInput,
  ) => Promise<PhaseResponse | null>;
  deletePhase: (phaseId: string) => Promise<boolean>;
  refresh: () => Promise<void>;
  clearError: () => void;
}

const extractPhases = (payload: unknown): PhaseResponse[] => {
  if (!payload || typeof payload !== "object") return [];
  // API returns either { phases: [...] } or directly an array
  const data =
    (payload as { phases?: PhaseResponse[]; data?: PhaseResponse[] }).phases ??
    (payload as { phases?: PhaseResponse[]; data?: PhaseResponse[] }).data ??
    payload;
  return Array.isArray(data) ? data : [];
};

export const useEstimatePhases = (
  estimateId: string | null,
): UseEstimatePhasesResult => {
  const queryClient = useQueryClient();
  const { getToken } = useAuthSafe();
  const { organizationId, queryEnabled, isOrganizationReady } =
    useOrganizationQueryGuard({
      enabled: !!estimateId,
      debugKey: "useEstimatePhases",
    });

  const api = createApiClient({
    getToken,
    getOrganizationId: () => organizationId ?? undefined,
    autoRetry429: true,
  });

  const queryKey = useMemo(() => ["estimate-phases", estimateId], [estimateId]);

  const phasesQuery = useQuery<PhaseResponse[]>({
    queryKey,
    enabled: queryEnabled,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: "always",
    gcTime: QUERY_GC_TIME.STANDARD,
    throwOnError: false,
    meta: { entity: "estimate-phases" },
    staleTime: QUERY_STALE_TIME.CRITICAL, // Phases need frequent updates
    queryFn: async () => {
      if (!estimateId || !organizationId || !isOrganizationReady) {
        throw new Error(
          "Still loading your organization. Give it a second and try again.",
        );
      }
      const res = await api.get<PhaseResponse[]>(
        `/api/estimates/${estimateId}/phases`,
      );

      if (isApiFailure(res)) {
        throw new Error(res.message);
      }

      const payload = res.data;
      const phases = extractPhases(payload);

      logger.info("Estimate phases fetched", {
        estimateId,
        count: phases.length,
      });

      return phases;
    },
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({
      queryKey,
      refetchType: "active",
    });
    await queryClient.refetchQueries({
      queryKey,
      type: "active",
      exact: true,
    });
  };

  const handleMutationError = (
    error: unknown,
    operation: "create" | "update" | "delete",
  ) => {
    const message =
      error instanceof Error ? error.message : "Unknown error occurred";
    logger.error(`Phase ${operation} error`, { error: message, estimateId });
    toast.error("Error", {
      description: `Failed to ${operation} phase: ${message}`,
    });
  };

  // Create phase mutation
  const createMutation = useMutation({
    mutationFn: async (input: PhaseCreateInput) => {
      if (!estimateId || !organizationId || !isOrganizationReady) {
        throw new Error(
          "Still loading your organization. Give it a second and try again.",
        );
      }
      const res = await api.post<PhaseResponse>(
        `/api/estimates/${estimateId}/phases`,
        input,
      );
      if (isApiFailure(res)) {
        toastFromApiFailure("Failed to create phase", res);
        throw new Error(res.message);
      }
      return res.data as PhaseResponse;
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<PhaseResponse[]>(queryKey);
      const optimistic: PhaseResponse = {
        phase_id: `temp-${Date.now()}`,
        organization_id: "",
        estimate_id: estimateId,
        project_id: null,
        takeoff_data_id: null,
        name: input.name,
        description: input.description ?? null,
        sort_order: (previous?.length ?? 0) + 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: null,
        updated_by: null,
        deleted_at: null,
      };
      queryClient.setQueryData<PhaseResponse[]>(queryKey, (old) => [
        ...(old ?? []),
        optimistic,
      ]);
      return { previous };
    },
    onError: (error, _input, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
      handleMutationError(error, "create");
    },
    onSuccess: () => {
      toast.success("Phase created");
    },
    onSettled: () => {
      invalidate();
    },
  });

  // Update phase mutation
  const updateMutation = useMutation({
    mutationFn: async ({
      phaseId,
      input,
    }: {
      phaseId: string;
      input: PhaseUpdateInput;
    }) => {
      if (!estimateId || !organizationId || !isOrganizationReady) {
        throw new Error(
          "Still loading your organization. Give it a second and try again.",
        );
      }
      const res = await api.put<PhaseResponse>(
        `/api/estimates/${estimateId}/phases/${phaseId}`,
        input,
      );
      if (isApiFailure(res)) {
        toastFromApiFailure("Failed to update phase", res);
        throw new Error(res.message);
      }
      return res.data as PhaseResponse;
    },
    onMutate: async ({ phaseId, input }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<PhaseResponse[]>(queryKey);
      queryClient.setQueryData<PhaseResponse[]>(queryKey, (old) =>
        (old ?? []).map((p) =>
          p.phase_id === phaseId
            ? { ...p, ...input, updated_at: new Date().toISOString() }
            : p,
        ),
      );
      return { previous };
    },
    onError: (error, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
      handleMutationError(error, "update");
    },
    onSuccess: () => {
      toast.success("Phase updated");
    },
    onSettled: () => {
      invalidate();
    },
  });

  // Delete phase mutation
  const deleteMutation = useMutation({
    mutationFn: async (phaseId: string) => {
      if (!estimateId || !organizationId || !isOrganizationReady) {
        throw new Error(
          "Still loading your organization. Give it a second and try again.",
        );
      }
      const res = await api.delete(
        `/api/estimates/${estimateId}/phases/${phaseId}`,
      );
      if (isApiFailure(res)) {
        toastFromApiFailure("Failed to delete phase", res);
        throw new Error(res.message);
      }
      return true;
    },
    onMutate: async (phaseId) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<PhaseResponse[]>(queryKey);
      queryClient.setQueryData<PhaseResponse[]>(queryKey, (old) =>
        (old ?? []).filter((p) => p.phase_id !== phaseId),
      );
      return { previous };
    },
    onError: (error, _phaseId, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
      handleMutationError(error, "delete");
    },
    onSuccess: () => {
      toast.success("Phase deleted");
    },
    onSettled: () => {
      invalidate();
    },
  });

  const refresh = async () => {
    await invalidate();
  };

  const phases = phasesQuery.data ?? [];
  const lastUpdated =
    phasesQuery.dataUpdatedAt > 0 ? new Date(phasesQuery.dataUpdatedAt) : null;
  const isLoading = phasesQuery.isLoading || phasesQuery.isFetching;
  const errorMessage =
    phasesQuery.error instanceof Error
      ? phasesQuery.error.message
      : phasesQuery.error
        ? String(phasesQuery.error)
        : null;

  const clearError = () => {
    queryClient.resetQueries({ queryKey });
  };

  return {
    phases,
    isLoading,
    error: errorMessage,
    lastUpdated,
    createPhase: async (input: PhaseCreateInput) => {
      try {
        return await createMutation.mutateAsync(input);
      } catch {
        return null;
      }
    },
    updatePhase: async (phaseId: string, input: PhaseUpdateInput) => {
      try {
        return await updateMutation.mutateAsync({ phaseId, input });
      } catch {
        return null;
      }
    },
    deletePhase: async (phaseId: string) => {
      try {
        return await deleteMutation.mutateAsync(phaseId);
      } catch {
        return false;
      }
    },
    refresh,
    clearError,
  };
};
